import { Button } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useCallback, useRef } from "react";
import ConfirmDialog from "../ConfirmDialog";
import { ConfirmDialogHandle } from "../ConfirmDialog";
import useUpdateHomeworkStatus from "../../hooks/api/homework/useUpdateHomeworkStatus";
import useEditHomework from "../../hooks/api/homework/useEditHomework";
import useHomeworkState from "../../state/homework.state";
import { encode } from "js-base64";
import { cuesToVTT } from "../../utils/vtt";
import { Homework } from "../../models/Homework";

type Props = {
  homework: Homework;
  refetchHomework: () => void;
};

export default function SubmitHomeworkButton({
  homework,
  refetchHomework,
}: Props) {
  const { t } = useTranslation();
  const {
    subtitles,
    captions,
    categories,
    voiceOvers,
    dubbings,
    audioDescriptions,
  } = useHomeworkState();
  const { mutateAsync: submitHomework, isPending: isSubmitHomeworkLoading } =
    useUpdateHomeworkStatus(homework.id);
  const { mutateAsync: editHomework, isPending: isEditHomeworkPending } =
    useEditHomework(homework.id);
  const confirmDialogRef = useRef<ConfirmDialogHandle>(null);

  const onSubmissionConfirm = useCallback(() => {
    editHomework({
      homework: {
        ...homework,
        subtitleVtt: encode(cuesToVTT(subtitles, categories)),
        captionVtt: encode(cuesToVTT(captions, categories)),
        categories,
        voiceOvers,
        dubbings,
        audioDescriptions,
      },
    }).then(() =>
      submitHomework({ status: { status: "DONE" } }).then(refetchHomework),
    );
  }, [
    audioDescriptions,
    captions,
    categories,
    dubbings,
    editHomework,
    homework,
    refetchHomework,
    submitHomework,
    subtitles,
    voiceOvers,
  ]);

  return (
    <>
      <Button
        bg="primary.DEFAULT"
        color="white"
        onClick={() => confirmDialogRef.current?.open()}
        isLoading={isSubmitHomeworkLoading || isEditHomeworkPending}
      >
        {t("common.buttons.submit")}
      </Button>
      <ConfirmDialog
        ref={confirmDialogRef}
        title={t("homework_page.submission")}
        message={t("homework_page.approval_message")}
        onConfirm={onSubmissionConfirm}
        isLoading={isSubmitHomeworkLoading || isEditHomeworkPending}
      />
    </>
  );
}
